import React, { Fragment } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    MenuItem,
    Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createRepository } from '../../apis/repository';
import { isEmpty } from 'lodash';

// Main validation schema
const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .required('Repository name is required')
        .min(2, 'Repository name must be at least 2 characters')
        .max(50, 'Repository name must be less than 50 characters'),

    type: Yup.string().required('Repository type is required').oneOf(['gcs', 's3', 'azure'], 'Invalid repository type'),

    credentials: Yup.mixed().when('type', (type, schema) =>
        type === 'gcs' ? schema.required('Credentials JSON file is required') : schema.notRequired()
    ),

    aws_access_key_id: Yup.string()
        .trim()
        .when('type', {
            is: 's3',
            then: (schema) => schema.required('AWS Access Key ID is required'),
            otherwise: (schema) => schema.notRequired(),
        }),

    aws_secret_access_key: Yup.string()
        .trim()
        .when('type', {
            is: 's3',
            then: (schema) => schema.required('AWS Secret Access Key is required'),
            otherwise: (schema) => schema.notRequired(),
        }),

    aws_region_name: Yup.string()
        .trim()
        .when('type', {
            is: 's3',
            then: (schema) => schema.required('AWS Region is required'),
            otherwise: (schema) => schema.notRequired(),
        }),

    azure_connection_string: Yup.string()
        .trim()
        .when('type', {
            is: 'azure',
            then: (schema) => schema.required('Azure connection string is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
});

const CreateRepositoryDialog = ({ open, onClose, onSuccess }) => {
    const formik = useFormik({
        initialValues: {
            name: '',
            type: 'gcs',
            credentials: null,
            gcs_file_name: null,
            aws_access_key_id: '',
            aws_secret_access_key: '',
            aws_region_name: '',
            azure_connection_string: '',
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                // Parse the credentials JSON and format for API
                const apiData = buildApiData(values);

                await createRepository(apiData);
                toast.success('Repository created successfully');
                onSuccess();
                handleClose();
            } catch (error) {
                let message = error || 'Failed to create repository';
                if (Array.isArray(error)) message = error.join(', ');
                toast.error(message);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleFileUpload = (file) => {
        if (!file) return;

        if (file.type !== 'application/json') {
            toast.error('Please select a valid JSON file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                // Validate JSON format
                const parsedJson = JSON.parse(event.target.result);

                formik.setFieldValue('credentials', parsedJson);
                formik.setFieldValue('gcs_file_name', file?.name);
                toast.success(`File "${file.name}" uploaded successfully`);
            } catch (error) {
                toast.error('Invalid JSON file format');
            }
        };
        reader.onerror = () => {
            toast.error('Error reading file');
        };
        reader.readAsText(file);
    };

    const handleClose = () => {
        formik.resetForm();

        // Clear file input
        const fileInput = document.getElementById('credentials-file-input');
        if (fileInput) fileInput.value = '';

        onClose();
    };

    const buildApiData = (values) => {
        const { type } = values;

        switch (type) {
            case 'gcs':
                return {
                    name: values.name,
                    type,
                    credentials: {
                        service_account_json: JSON.stringify(values.credentials),
                    },
                };

            case 's3':
                return {
                    name: values.name,
                    type,
                    credentials: {
                        aws_access_key_id: values.aws_access_key_id,
                        aws_secret_access_key: values.aws_secret_access_key,
                        region_name: values.aws_region_name,
                    },
                };

            case 'azure':
                return {
                    name: values.name,
                    type,
                    credentials: {
                        connection_string: values.azure_connection_string,
                    },
                };

            default:
                throw new Error('Unsupported repository type');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: 600,
                    py: 2,
                    px: 0,
                }}
            >
                Create New Repository
                <IconButton
                    onClick={() => handleClose()}
                    disabled={formik.isSubmitting}
                    sx={{
                        p: 1,
                    }}
                >
                    <CloseIcon sx={{ fontSize: '1.25rem', color: '#666' }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: '2rem 0' }}>
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Repository Name */}
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                Repository Name *
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                disabled={formik.isSubmitting}
                                placeholder="Enter repository name"
                            />
                        </Box>

                        {/* Repository Type */}
                        <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                                Repository Type *
                            </Typography>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                name="type"
                                value={formik.values.type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.type && Boolean(formik.errors.type)}
                                helperText={formik.touched.type && formik.errors.type}
                                disabled={formik.isSubmitting}
                            >
                                <MenuItem value="gcs">Google Cloud Storage</MenuItem>
                                <MenuItem value="s3">AWS S3 Bucket</MenuItem>
                                <MenuItem value="azure">Azure Blob Storage</MenuItem>
                            </TextField>
                        </Box>

                        {/* Credentials */}

                        {formik.values.type === 'gcs' ? (
                            <GcsCredentials formik={formik} handleFileUpload={handleFileUpload} />
                        ) : formik.values.type === 's3' ? (
                            <S3Credentials formik={formik} />
                        ) : (
                            <AzureCredentials formik={formik} />
                        )}
                    </Box>
                </form>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ gap: 1 }}>
                <Button variant="outlined" color="secondary" onClick={() => handleClose()} disabled={formik.isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    // onClick={formik.handleSubmit}
                    onClick={() => {
                        formik.validateForm().then(() => {
                            formik.setTouched({
                                name: true,
                                type: true,
                                credentials: true,
                                aws_access_key_id: true,
                                aws_secret_access_key: true,
                                aws_region_name: true,
                                azure_connection_string: true,
                            });
                            formik.handleSubmit();
                        });
                    }}
                    disabled={formik.isSubmitting}
                    type="submit"
                >
                    {formik.isSubmitting ? 'Creating...' : 'Create Repository'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRepositoryDialog;

const GcsCredentials = ({ formik, handleFileUpload }) => {
    return (
        <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                Credentials (JSON File) *
            </Typography>
            <Box
                sx={{
                    border: '.125rem dashed #E1E0EB',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    color: '#6b7280',
                    backgroundColor: '#F8F7F9',
                    fontFamily: 'Inter',
                    cursor: formik.isSubmitting ? 'not-allowed' : 'pointer',
                    '&:hover': { backgroundColor: formik.isSubmitting ? '#F8F7F9' : '#f9fafb' },
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    if (formik.isSubmitting) return;
                    const file = e.dataTransfer.files[0];
                    handleFileUpload(file);
                }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => {
                    if (!formik.isSubmitting) document.getElementById('credentials-file-input').click();
                }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 40, color: '#6A677E' }}>
                    {formik.values.credentials ? 'check_circle' : 'Upload'}
                </span>
                <Typography variant="body2">
                    {formik.values.credentials
                        ? 'JSON file uploaded successfully'
                        : 'Click to upload or drag and drop JSON file'}
                </Typography>

                {!isEmpty(formik.values.gcs_file_name) ? (
                    <Typography variant="body2" color="primary">
                        {formik.values.gcs_file_name}
                    </Typography>
                ) : null}

                <input
                    id="credentials-file-input"
                    type="file"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files[0];
                        handleFileUpload(file);
                    }}
                    disabled={formik.isSubmitting}
                />
            </Box>
            {formik.touched.credentials && formik.errors.credentials && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.credentials}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Upload your cloud storage credentials JSON file
            </Typography>
        </Box>
    );
};

const S3Credentials = ({ formik }) => {
    return (
        <Fragment>
            <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                    AWS Access Key Id *
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    name="aws_access_key_id"
                    value={formik.values.aws_access_key_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.aws_access_key_id && Boolean(formik.errors.aws_access_key_id)}
                    helperText={formik.touched.aws_access_key_id && formik.errors.aws_access_key_id}
                    disabled={formik.isSubmitting}
                    placeholder="Enter access key id"
                />
            </Box>

            <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                    AWS Secret Access Key *
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    name="aws_secret_access_key"
                    value={formik.values.aws_secret_access_key}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.aws_secret_access_key && Boolean(formik.errors.aws_secret_access_key)}
                    helperText={formik.touched.aws_secret_access_key && formik.errors.aws_secret_access_key}
                    disabled={formik.isSubmitting}
                    placeholder="Enter secret access key"
                />
            </Box>

            <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                    Region Name *
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    name="aws_region_name"
                    value={formik.values.aws_region_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.aws_region_name && Boolean(formik.errors.aws_region_name)}
                    helperText={formik.touched.aws_region_name && formik.errors.aws_region_name}
                    disabled={formik.isSubmitting}
                    placeholder="Enter region name"
                />
            </Box>
        </Fragment>
    );
};

const AzureCredentials = ({ formik }) => {
    return (
        <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#333' }}>
                Connection String *
            </Typography>
            <TextField
                fullWidth
                size="small"
                name="azure_connection_string"
                value={formik.values.azure_connection_string}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.azure_connection_string && Boolean(formik.errors.azure_connection_string)}
                helperText={formik.touched.azure_connection_string && formik.errors.azure_connection_string}
                disabled={formik.isSubmitting}
                placeholder="Enter connection string"
            />
        </Box>
    );
};
